// Full-document shell for pillows pages rendered entirely through React.
// The in-tree SiteHeader renders the flash messages itself. reactivated's
// renderer injects the bundle CSS/JS + __PRELOADED_PROPS__, so we deliberately
// emit none of that here.
import React from "react";
import { ErrorBoundary } from "tsi-common-react/src/components/ErrorBoundary/index";
import { MetaTags } from "tsi-common-react/src/components/MetaTags/index";
import {
    initPromoCalloutMessages,
    useFocusFirstFormError,
    useLinkRewrites,
    useRichTextDecorators,
} from "tsi-common-react/src/utils/richTextDecorators";
import { watchForWistia } from "tsi-common-react/src/utils/wistia";
import { t } from "ttag";

import { Context, templates } from "@reactivated";

import { ExternalScript } from "@thelabnyc/thelabui/src/components/ExternalScript";
import { useSSRNonce } from "@thelabnyc/thelabui/src/hooks/useSSRNonce";
import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { Template as SessionKeepAlive } from "../../templates/SessionKeepAliveTemplate";
import { SiteFooter } from "../SiteFooter";
import { SiteHeader } from "../SiteHeader";

// Per-page chrome (title, meta, canonical, JSON-LD, body_class, flash messages)
// carried as the `page_chrome` field on each full-page @template. The global
// document shell (head/body globals, header, footer) comes from the `chrome`
// context processor (useContext below), not from these props.
//
// Typing `pageChrome` to the shared PageChrome (via UtilityPageTemplate) lets
// every full-document page template reuse PageSkeleton (UtilityPage,
// ContactFormPage, FormPage, ...) regardless of their other props.
type IProps = {
    pageChrome: templates.UtilityPageTemplate["page_chrome"];
    children?: React.ReactNode;
    // Header/footer slots (siblings of <main> inside .layout, matching the
    // legacy tempur.html block structure that puppeteer keys off of). A node
    // renders in place of the default <SiteHeader>/<SiteFooter>; `false` omits
    // the slot entirely; `undefined` (default) renders the site chrome, so
    // existing full-document pages are unaffected.
    header?: React.ReactNode | false;
    footer?: React.ReactNode | false;
};

const UnsupportedWarning = ({
    staticBase,
}: {
    staticBase: string;
}): React.JSX.Element => {
    const browsers = [
        {
            title: t`Get Google Chrome`,
            href: "http://www.google.com/chrome",
            img: "tsicommon_cms/img/chrome_32x32.png",
            label: t`Google Chrome`,
        },
        {
            title: t`Get Mozilla Firefox`,
            href: "http://www.firefox.com",
            img: "tsicommon_cms/img/firefox_32x32.png",
            label: t`Mozilla Firefox`,
        },
        {
            title: t`Get Apple Safari`,
            href: "http://www.apple.com/safari/download",
            img: "tsicommon_cms/img/safari_32x32.png",
            label: t`Apple Safari`,
        },
        {
            title: t`Get Microsoft Edge`,
            href: "https://www.microsoft.com/en-us/edge",
            img: "tsicommon_cms/img/edge_32x32.png",
            label: t`Microsoft Edge`,
        },
    ];
    return (
        <>
            <div className="unsupported-browser-container">
                <div className="unsupported-browser">
                    {/* eslint-disable-next-line react/no-unknown-property */}
                    <a
                        className="unsupported-browser__close-btn"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const container =
                                e.currentTarget.parentNode?.parentNode;
                            if (container instanceof HTMLElement) {
                                container.style.display = "none";
                            }
                        }}
                    >
                        {t`Close`}
                    </a>
                    <div className="unsupported-browser__title">
                        {t`Improve Your Shopping Experience`}
                    </div>
                    <p className="unsupported-browser__copy">
                        {t`You’re using an unsupported web browser.`}
                        <br />
                        {t`A list of the most popular browsers can be found below. Just click on an icon to visit the download page.`}
                    </p>
                    <ul className="unsupported-browser__list">
                        {browsers.map((b) => (
                            <li
                                className="unsupported-browser__list-item"
                                key={b.img}
                            >
                                <a
                                    title={b.title}
                                    href={b.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        src={`${staticBase}${b.img}`}
                                        alt={b.title}
                                        title={b.title}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <br />
                                    {b.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="unsupported-mobileos-container">
                <div className="unsupported-mobileos">
                    <a
                        className="unsupported-mobileos__close-btn"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const container =
                                e.currentTarget.parentNode?.parentNode;
                            if (container instanceof HTMLElement) {
                                container.style.display = "none";
                            }
                        }}
                    >
                        {t`Close`}
                    </a>
                    <div className="unsupported-mobileos__title">
                        {t`Your Software is Out of Date`}
                    </div>
                    <p className="unsupported-mobileos__copy">
                        {t`Your phone or tablet is using software unsupported by our website. To improve your shopping experience, please update your device to the latest version of iOS or Android OS.`}
                    </p>
                </div>
            </div>
        </>
    );
};

export const PageSkeleton = (props: IProps): React.JSX.Element => {
    React.useEffect(() => watchForWistia(), []);
    const { pageChrome, children, header, footer } = props;
    // Global chrome (header/nav, footer, head/body globals) comes from the
    // `chrome` context processor. PageSkeleton is always the full-document root
    // inside the reactivated Provider, so the context is populated in both SSR
    // and client hydration. This read is intentionally unguarded: callers
    // rendering outside a chrome-carrying Provider (stories/tests) must supply
    // a chrome fixture in their Context value.
    const context = React.useContext(Context);
    const chrome = context.chrome;
    const nonce = useSSRNonce();
    // Post-hydration DOM decorators shared by every full-document page: fill CMS
    // rich-text promo tokens, apply the link-rewrite ruleset, and focus the
    // first form error.
    useRichTextDecorators([initPromoCalloutMessages]);
    useLinkRewrites();
    useFocusFirstFormError();
    // Two <html> mutations that legacy JS does eagerly (pre-hydration) must be
    // deferred to after hydration commits, because React owns <html> in this
    // full-document render and an early imperative change races hydration and
    // mismatches:
    //   1. `no-js` -> dropped once JS is confirmed running (index.tsx removes it
    //      eagerly only on island pages; here React renders it for the no-JS
    //      fallback, so we drop it post-hydration instead).
    //   2. `data-useragent` -> drives the unsupported-browser CSS. It's per-user
    //      and these pages are cacheable, so it must NOT be server-rendered.
    // React owns <html> but carries neither in its vdom, so there is nothing to
    // reconcile once set here. legacy.js still does both for old browsers that
    // never boot React (see legacy.tsx's module-support guard).
    React.useEffect(() => {
        const win = getWindow();
        const doc = getDocument();
        if (doc?.documentElement) {
            doc.documentElement.classList.remove("no-js");
            if (win?.navigator) {
                doc.documentElement.setAttribute(
                    "data-useragent",
                    win.navigator.userAgent,
                );
            }
        }
    }, []);
    // The OneTrust callback stub stays out of the render tree: it only has to
    // exist before the async SDK finishes loading and calls it, so defining it
    // post-hydration keeps the GTM bootstrap the first script vnode in <body>.
    React.useEffect(() => {
        const win = getWindow() as
            | (Window & { OptanonWrapper?: () => void })
            | null;
        if (!win) return;
        if (!("OptanonWrapper" in win)) {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            win.OptanonWrapper = function () {};
        }
    }, []);
    return (
        <html className="no-js" lang={chrome.html_lang}>
            <head>
                {/*
                 * tempur.html uses an inline `html{visibility:hidden;opacity:0}`
                 * FOUC guard, relying on the bundle CSS (`html{visibility:visible}`)
                 * overriding it because the bundle <link> comes AFTER it in source
                 * order. In a full-document React render, React 19 hoists the bundle
                 * stylesheet to the TOP of <head> (before any inline <style>), which
                 * would invert that order and leave the page permanently hidden. The
                 * bundle <link> is render-blocking in <head> here, so there is no
                 * FOUC to guard against -- we intentionally omit the inline hide.
                 */}
                <title>{pageChrome.title}</title>
                <meta
                    httpEquiv="content-type"
                    content="text/html; charset=UTF-8"
                />
                <meta name="created" content={chrome.created} />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0"
                />
                <meta
                    name="google-site-verification"
                    content={chrome.google_site_verification}
                />
                {pageChrome.search_description && (
                    <meta
                        name="description"
                        content={pageChrome.search_description}
                    />
                )}
                {pageChrome.search_keywords && (
                    <meta
                        name="keywords"
                        content={pageChrome.search_keywords}
                    />
                )}
                {pageChrome.robots_noindex && (
                    <meta name="robots" content="noindex" />
                )}
                <link rel="canonical" href={pageChrome.canonical_url} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:site" content={chrome.twitter_site} />
                <meta name="twitter:title" content={pageChrome.seo_heading} />
                <meta
                    name="twitter:description"
                    content={pageChrome.search_description}
                />
                <meta name="twitter:url" content={pageChrome.current_url} />
                {pageChrome.twitter_image && (
                    <meta
                        name="twitter:image"
                        content={pageChrome.twitter_image}
                    />
                )}
                <meta property="og:title" content={pageChrome.seo_heading} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={pageChrome.current_url} />
                <meta
                    property="og:description"
                    content={pageChrome.search_description}
                />
                <meta property="og:site_name" content={chrome.og_site_name} />
                {pageChrome.og_image && (
                    <meta property="og:image" content={pageChrome.og_image} />
                )}
                <link
                    rel="shortcut icon"
                    type="image/x-icon"
                    href={chrome.favicon_url}
                />
                {/* JSON-LD ({% google_page_schema %}) is rendered at the top of
                    <body> -- see the head comment above and the body comment. */}
            </head>
            <body
                id={chrome.body_id}
                className={pageChrome.body_class}
                data-appslug={chrome.app_slug}
                data-version={chrome.version}
                data-environment={chrome.environment}
                data-sentry-dsn={chrome.sentry_dsn}
                data-track-page-type={pageChrome.page_type}
                data-view-name={chrome.view_name}
            >
                {chrome.aws_waf_url && (
                    <ExternalScript src={chrome.aws_waf_url} defer={true} />
                )}
                {/*
                 * Top of <body>, not <head>: React 19 hoists the <meta> and
                 * async <script src> children up, while an inline <script> in
                 * the React-owned <head> gets paired with whatever a third party
                 * injects there before hydration finishes.
                 */}
                <MetaTags
                    tags={context.meta_tags.meta_tags.value}
                    doNotTrack={chrome.do_not_track}
                />
                {/*
                 * JSON-LD is emitted as SSR-only markup: the raw
                 * <script type="application/ld+json"> tags are inserted as a
                 * <div>'s innerHTML, so they appear in the server-rendered HTML
                 * for crawlers but are never React vnodes -- never positionally
                 * tracked during hydration. (Scripts inserted via innerHTML never
                 * execute per the HTML5 spec, which is fine: JSON-LD carries data
                 * only.)
                 */}
                {pageChrome.json_ld.length > 0 && (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: pageChrome.json_ld
                                .map(
                                    (schema) =>
                                        `<script type="application/ld+json">${schema}</script>`,
                                )
                                .join(""),
                        }}
                    />
                )}
                <div className="layout">
                    {header === undefined ? (
                        <SiteHeader
                            {...chrome.site_header}
                            messages={pageChrome.messages}
                        />
                    ) : (
                        header
                    )}
                    <main id="main-content">
                        <ErrorBoundary>{children}</ErrorBoundary>
                    </main>
                    {footer === undefined ? (
                        <SiteFooter
                            {...chrome.footer}
                            staticBase={chrome.static_base}
                        />
                    ) : (
                        footer
                    )}
                </div>
                <SessionKeepAlive />
                <script
                    defer={true}
                    src={`${chrome.static_base}dist2/legacy.js`}
                    nonce={nonce}
                    suppressHydrationWarning={true}
                />
                <UnsupportedWarning staticBase={chrome.static_base} />
            </body>
        </html>
    );
};
